import { useRef, useMemo } from 'react';
import { isUndefined } from 'lodash';
import { DIRECTIONS, INDICATOR_DOTS_NUMBER } from '../constants';

interface Params {
    currentPosition: number;
    slidesToShow: number;
    slides: any[];
}

interface GetDirectionParams {
    prevPosition?: number;
    currentPosition: Params['currentPosition'];
    slidesToShow: Params['slidesToShow'];
    maxIdx: number;
}

interface CalcVisibleDotRangesParams extends GetDirectionParams {
    currentRange: { startIndex: number, endIndex: number };
    slides: Params['slides'];
}

interface MapSlideDotStateItemParams
    extends Pick<GetDirectionParams, 'currentPosition' | 'maxIdx'>,
    Pick<CalcVisibleDotRangesParams['currentRange'], 'startIndex' | 'endIndex'> {
    index: number;
}

const isDirectionRight = (direction?: DIRECTIONS) => direction === DIRECTIONS.RIGHT;
const isDirectionLeft = (direction?: DIRECTIONS) => direction === DIRECTIONS.LEFT;

const getDirection = ({
    prevPosition,
    currentPosition,
    slidesToShow,
    maxIdx
}: GetDirectionParams): DIRECTIONS | undefined => {
    const scrollFromFirstToLast = currentPosition === maxIdx && prevPosition === 0;
    const scrollFromLastToFirst = currentPosition === 0 && prevPosition === maxIdx;
    let direction;

    if (!isUndefined(prevPosition)) {
        direction = currentPosition > prevPosition ? DIRECTIONS.RIGHT : DIRECTIONS.LEFT;
        if (scrollFromFirstToLast) {
            // with infinity
            direction = DIRECTIONS.LEFT;
        }
        if (scrollFromLastToFirst) {
            // with infinity
            direction = DIRECTIONS.RIGHT;
        }
    } else if (!direction && currentPosition !== 0) {
        direction = currentPosition === slidesToShow ? DIRECTIONS.RIGHT : DIRECTIONS.LEFT;
    }
    return direction;
};

const calcVisibleDotRanges = ({
    currentRange,
    prevPosition,
    currentPosition,
    slidesToShow,
    slides,
    maxIdx
}: CalcVisibleDotRangesParams) => {
    const direction = getDirection({ prevPosition, currentPosition, slidesToShow, maxIdx });
    let { startIndex, endIndex } = { ...currentRange };
    if (isDirectionLeft(direction) && currentPosition + 1 === startIndex) {
        startIndex -= 1;
        endIndex -= 1;
    } else if (isDirectionRight(direction) && currentPosition - 1 === endIndex) {
        startIndex += 1;
        endIndex += 1;
    } else if (isDirectionLeft(direction) && startIndex === 0 && currentPosition === maxIdx) { // infinity nav back to end
        startIndex = Math.max(slides.length - INDICATOR_DOTS_NUMBER, 0);
        endIndex = maxIdx;
    } else if (isDirectionRight(direction) && endIndex === maxIdx && currentPosition === 0) { // infinity nav next to start
        startIndex = 0;
        endIndex = INDICATOR_DOTS_NUMBER - 1;
    }

    return { startIndex, endIndex };
};

const mapSlideDotStateItem = ({ index, currentPosition, startIndex, endIndex, maxIdx }: MapSlideDotStateItemParams) => {
    const active = index === currentPosition;
    let edgeDot, hiddenDot;

    if (startIndex !== 0 && endIndex !== maxIdx) {
        edgeDot = index === startIndex - 1 || index === endIndex + 1;
        hiddenDot = index < startIndex - 1 || index > endIndex + 1;
    } else if (startIndex === 0) {
        edgeDot = index === INDICATOR_DOTS_NUMBER;
        hiddenDot = index > INDICATOR_DOTS_NUMBER;
    } else if (endIndex === maxIdx) {
        edgeDot = index === maxIdx - INDICATOR_DOTS_NUMBER;
        hiddenDot = index < Math.max(maxIdx - INDICATOR_DOTS_NUMBER, 0);
    }

    return { index, hiddenDot, edgeDot, active };
};

export const useDotsIndicators = ({ currentPosition, slidesToShow, slides }: Params) => {
    const prevPosition = useRef<number>();
    const range = useRef({ startIndex: 0, endIndex: INDICATOR_DOTS_NUMBER - 1 });

    const data = useMemo(() => {
        const maxIdx = slides.length - 1;
        const { startIndex, endIndex } = calcVisibleDotRanges({
            currentRange: { ...range.current },
            prevPosition: prevPosition.current,
            currentPosition,
            slides,
            slidesToShow,
            maxIdx
        });

        const items = slides.map((_, index) =>
            mapSlideDotStateItem({ index, currentPosition, startIndex, endIndex, maxIdx })
        );

        return {
            range: { startIndex, endIndex },
            edgeDotsIdx: items.filter((dot) => dot.edgeDot).map((dot) => dot.index),
            hiddenDotsIdx: items.filter((dot) => dot.hiddenDot).map((dot) => dot.index)
        };
    }, [slides, currentPosition, slidesToShow, prevPosition, range]);

    range.current = { ...data.range };
    prevPosition.current = currentPosition;

    return {
        edgeDotsIdx: data.edgeDotsIdx,
        hiddenDotsIdx: data.hiddenDotsIdx
    };
};
